export class Gateway {
  public original: Gateway;

  constructor(
    public name: string,
    public serialNumber: string,
    public address: string,
    public buildingId: number,
    public id?: number,
    public versionNumber: number = GatewayVersion.VERSIONTHREE.value,
    public isDefaultNetwork: boolean = true,
    public isActive: boolean = false,
    public staticIpAddress?: string,
    public gatewayIpAddress?: string,
    public isDefaultDns: boolean = true,
    public dnsServers?: string,
    public subnetMask: string = '255.255.255.0',
    public networkType: string = NetworkType.WIREPAS.value,
    public firmwareVersionId?: number,
    public gatewaySnap?: string,
    public gatewayType: string = GatewayType.GATEWAYONEPOINTTWO.value,
    public gatewayHealthLogLevel: string = LogLevel.INFO.value,
    public iotLogLevel: string = LogLevel.INFO.value,
    public isAwsIotEnabled: boolean = true,
    public otapLogLevel: string = LogLevel.INFO.value,
    public isOtapEnabled?: boolean,
    public isWntEnabled?: boolean,
    public wntLogLevel: string = LogLevel.INFO.value,
    public bacnetLogLevel: string = LogLevel.INFO.value,
    public bacnetDeviceId?: number,
    public bacnetPort?: number,
    public isBacnetEnabled?: boolean,
    public hasKeys?: boolean,
    public lastRestart?: string,
    public lastUpdated?: string,
    public awsRegion?: string,
    public hasMatchingBuildingAWSRegion?: boolean,
    public installedSnapRevision?: string,
    public supportsNmsFirmware?: boolean
  ) {}

  public static clone(other: Gateway): Gateway {
    return new Gateway(
      other.name,
      other.serialNumber,
      other.address,
      other.buildingId,
      other.id,
      other.versionNumber,
      NetworkType.WIREPAS.equalsString(other.networkType) ? other.isDefaultNetwork : null,
      other.isActive,
      NetworkType.SILVAIR.equalsString(other.networkType) || other.isDefaultNetwork ? null : other.staticIpAddress,
      NetworkType.SILVAIR.equalsString(other.networkType) || other.isDefaultNetwork ? null : other.gatewayIpAddress,
      other.isDefaultDns,
      NetworkType.SILVAIR.equalsString(other.networkType) || other.isDefaultNetwork || !other.dnsServers
        ? null
        : other.dnsServers.replace(/ /g, ''),
      NetworkType.SILVAIR.equalsString(other.networkType) || other.isDefaultNetwork ? null : other.subnetMask,
      other.networkType,
      NetworkType.SILVAIR.equalsString(other.networkType) ? null : other.firmwareVersionId,
      other.gatewaySnap,
      NetworkType.SILVAIR.equalsString(other.networkType) ? null : other.gatewayType,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.gatewayHealthLogLevel,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.iotLogLevel,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.isAwsIotEnabled,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.otapLogLevel,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.isOtapEnabled,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.isWntEnabled,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.wntLogLevel,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.bacnetLogLevel,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.bacnetDeviceId,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.bacnetPort,
      NetworkType.SILVAIR.equalsString(other.networkType) || GatewayType.VESTA.equalsString(other.networkType)
        ? null
        : other.isBacnetEnabled,
      other.hasKeys,
      this.humanizeTimestamp(other.lastRestart),
      this.humanizeTimestamp(other.lastUpdated),
      other.awsRegion,
      other.hasMatchingBuildingAWSRegion,
      other.installedSnapRevision,
      other.supportsNmsFirmware
    );
  }

  public static humanizeTimestamp(unixtime): any {
    if (!unixtime) {
      return 'N/A';
    }
    return new Date(unixtime * 1000);
  }
}

export class NetworkType {
  public static SILVAIR = new NetworkType('SILVAIR', 'Silvair');
  public static WIREPAS = new NetworkType('WIREPAS', 'Wirepas');

  public static all(): NetworkType[] {
    return [NetworkType.SILVAIR, NetworkType.WIREPAS];
  }

  public static fromValue(value: string): NetworkType {
    return value === NetworkType.SILVAIR.value ? NetworkType.SILVAIR : NetworkType.WIREPAS;
  }
  public constructor(public value: _NetworkType, public name: string) {}

  public equals(other: NetworkType): boolean {
    return this.equalsString(other.value);
  }

  public equalsString(other: string): boolean {
    return other !== null && other === this.value;
  }

  public toString(): string {
    return this.value;
  }
}

export enum GatewaySnap {
  ORT_WIREPAS = 'ort-wirepas',
  RIGADO_WIREPAS = 'rigado-wirepas'
}

export class FirmwareVersion {
  public constructor(public id: number, public version: string, public legacySupport: boolean, public nmsSupport: boolean) {}
  private static versions: FirmwareVersion[] = [];

  public static addVersion(id: number, version: string, legacySupport: boolean, nmsSupport: boolean): void {
    if (!this.versions.some((v) => v.version === version)) {
      this.versions.push(new FirmwareVersion(id, version, legacySupport, nmsSupport));
    }
  }

  public static all(): FirmwareVersion[] {
    return [...this.versions];
  }

  public static fromValue(value: string): FirmwareVersion | undefined {
    return this.versions.find((v) => v.version === value);
  }

  public static fromId(id: number): FirmwareVersion | undefined {
    return this.versions.find((v) => v.id === id);
  }

  public equals(other: FirmwareVersion): boolean {
    return this.equalsString(other.version);
  }

  public equalsString(other: string): boolean {
    return other !== null && other === this.version;
  }

  public toString(): string {
    return this.version;
  }
}

export class GatewayType {
  public static VESTA = new GatewayType('VESTA', 'Vesta');
  public static GATEWAYONEPOINTONE = new GatewayType('LINUX_GATEWAY_1_1', 'Linux Gateway 1.1');
  public static GATEWAYONEPOINTTWO = new GatewayType('LINUX_GATEWAY_1_2', 'Linux Gateway 1.2');

  public static all(): GatewayType[] {
    return [GatewayType.VESTA, GatewayType.GATEWAYONEPOINTONE, GatewayType.GATEWAYONEPOINTTWO];
  }

  public static fromValue(value: string): GatewayType {
    switch (value) {
      case GatewayType.VESTA.value:
        return GatewayType.VESTA;
      case GatewayType.GATEWAYONEPOINTONE.value:
        return GatewayType.GATEWAYONEPOINTONE;
      case GatewayType.GATEWAYONEPOINTTWO.value:
        return GatewayType.GATEWAYONEPOINTTWO;
    }
  }
  public constructor(public value: _GatewayType, public name: string) {}

  public equals(other: GatewayType): boolean {
    return this.equalsString(other.value);
  }

  public equalsString(other: string): boolean {
    return other !== null && other === this.value;
  }

  public toString(): string {
    return this.value;
  }
}

export class GatewayVersion {
  public static VERSIONTWO = new GatewayVersion(2, '2');
  public static VERSIONTHREE = new GatewayVersion(3, '3');

  public static all(): GatewayVersion[] {
    return [GatewayVersion.VERSIONTWO, GatewayVersion.VERSIONTHREE];
  }

  public static fromValue(value: number): GatewayVersion {
    switch (value) {
      case GatewayVersion.VERSIONTWO.value:
        return GatewayVersion.VERSIONTWO;
      case GatewayVersion.VERSIONTHREE.value:
        return GatewayVersion.VERSIONTHREE;
    }
  }
  public constructor(public value: _GatewayVersion, public name: string) {}

  public equals(other: GatewayVersion): boolean {
    return this.equalsString(other.name);
  }

  public equalsString(other: string): boolean {
    return other !== null && other === this.name;
  }

  public toString(): string {
    return this.name;
  }
}

export class LogLevel {
  public static ERROR = new LogLevel('ERROR', 'Error');
  public static DEBUG = new LogLevel('DEBUG', 'Debug');
  public static INFO = new LogLevel('INFO', 'Info');

  public static all(): LogLevel[] {
    return [LogLevel.ERROR, LogLevel.DEBUG, LogLevel.INFO];
  }

  public static fromValue(value: string): LogLevel {
    switch (value) {
      case LogLevel.ERROR.value:
        return LogLevel.ERROR;
      case LogLevel.DEBUG.value:
        return LogLevel.DEBUG;
      case LogLevel.INFO.value:
        return LogLevel.INFO;
    }
  }
  public constructor(public value: _LogLevel, public name: string) {}

  public equals(other: LogLevel): boolean {
    return this.equalsString(other.value);
  }

  public equalsString(other: string): boolean {
    return other !== null && other === this.value;
  }

  public toString(): string {
    return this.value;
  }
}

export class AWSRegion {
  private readonly regionLabel: string;
  private readonly regionName: string;
  private readonly regionCode: string;

  private constructor(regionLabel: string, regionName: string, regionCode: string) {
    this.regionLabel = regionLabel;
    this.regionName = regionName;
    this.regionCode = regionCode;
  }

  static SYDNEY = new AWSRegion('SYDNEY', 'Sydney', 'ap-southeast-2');
  static TOKYO = new AWSRegion('TOKYO', 'Tokyo', 'ap-northeast-1');
  static IRELAND = new AWSRegion('IRELAND', 'Ireland', 'eu-west-1');

  static all(): AWSRegion[] {
    return [AWSRegion.SYDNEY, AWSRegion.TOKYO, AWSRegion.IRELAND];
  }

  static euRegions(): AWSRegion[] {
    return [AWSRegion.IRELAND];
  }

  static fromCode(code: string): AWSRegion | null {
    const regions = AWSRegion.euRegions();
    for (const region of regions) {
      if (region.regionLabel === code) {
        return region;
      }
    }
    return null;
  }

  getName(): string {
    return this.regionName;
  }
}

type _NetworkType = 'SILVAIR' | 'WIREPAS';
type _GatewayVersion = 2 | 3;
type _GatewayType = 'VESTA' | 'LINUX_GATEWAY_1_1' | 'LINUX_GATEWAY_1_2';
type _LogLevel = 'ERROR' | 'DEBUG' | 'INFO';
